import { checkIfMerchant, getTradeType } from "@shared/src/utils";
import { cn } from "@shared/src/utils/cn";
import { Button } from "@shared/src/components/ui/button";
import { TextWithCopy } from "@shared/src/components/common/misc/TextWithCopy";
import { getTargetDate } from "@shared/src/utils/order";
import { OrderResponse } from "@sdk/shared/types/order.types";
import { Chip } from "@shared/src/components/ui/chip";
import ViewDetailsButton from "@shared/src/components/common/buttons/viewDetailsButton";
import { CountdownTimer } from "@shared/src/components/common/misc/CountdownTimer";
import { ORDER_PAGE } from "@shared/src/config/page-links";
import { useState } from "react";
import { ConfirmationDialog } from "@shared/src/components/common/dialogs/ConfirmationDialog";
import { X } from "lucide-react";
import { getStatusForState } from "@shared/src/lib/functions";
import useOrderExecutor from "@shared/src/components/modules/p2p/useOrderExecutor";
import { formatNumber } from "@shared/src/utils/helpers";

interface PendingOrderCardProps {
  order: OrderResponse;
  context?: "dropdown" | "sheet" | "nested-dropdown";
  onNavigate?: () => void;
}

export function PendingOrderCard({ order, context = "dropdown", onNavigate }: PendingOrderCardProps) {
  const href = ORDER_PAGE(order.id);
  const execute = useOrderExecutor();
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  function handleAction(action: "accept" | "cancel") {
    if (action === "cancel") {
      setShowRejectDialog(true);
    } else {
      execute({
        action: action === "accept" ? "acceptOrder" : "cancelOrder",
        orderData: order,
      });
    }
  }

  const handleConfirmReject = () => {
    setShowRejectDialog(false);
    execute({
      action: "cancelOrder",
      orderData: order,
    });
  };

  const { tradeType, isMerchant } = getTradeType(order.type, order.merchant.address);
  const canAccept = isMerchant && order.state === "intent";
  const targetDate = getTargetDate({
    orderState: order.state,
    orderDuration: order.duration,
    orderExpiry: order.expiry,
  });

  const user = isMerchant ? order.trader : order.merchant;

  return (
    <>
      <div className="flex flex-col p-4 rounded-2xl border-2 border-surfaces-bg-tertiary">
        <div className="flex items-center justify-between mb-2">
          <div className="space-y-1">
            <p className="text-text-primary font-extrabold">
              {user.account?.username || user.address.substring(0, 6) + "..." + user.address.substring(38)}
            </p>
            <span className="text-text-secondary text-small">
              Order Number: #<TextWithCopy text={order.id} />
            </span>
            <Chip className="w-fit">{getStatusForState(order.state)}</Chip>
          </div>

          <div className="text-right space-y-1">
            {tradeType === "buy" ? (
              <span className="text-success-500 font-bold"> Buy</span>
            ) : (
              <span className="text-error-500 font-bold"> Sell</span>
            )}
            <p className="text-text-primary font-extrabold">
              {order.quantity} {order.token.symbol}
            </p>
            <p className="text-text-secondary text-small">
              {formatNumber(order.quantity * order.price)} {order.currency.symbol}
            </p>
            <p className="text-small text-text-primary">{order.paymentMethod.name}</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4">
          <div className="flex w-full gap-2 md:w-auto">
            <Chip
              variant="secondary"
              size="md"
              className="!h-12 bg-surfaces-bg-tertiary flex items-center px-6 py-2.5 rounded-2xl"
            >
              Expires in: <CountdownTimer targetDate={targetDate} />
            </Chip>
            <ViewDetailsButton href={href} onClick={onNavigate} />
          </div>
          {canAccept && (
            <div className="flex w-full gap-2 md:w-auto">
              <Button
                variant="secondary"
                size="md"
                radius="full"
                onClick={() => handleAction("cancel")}
                className="!h-12 px-4 md:px-6 py-2.5 flex-1 max-w-[160px]"
              >
                Reject
              </Button>
              <Button
                variant="primary"
                size="md"
                radius="full"
                onClick={() => handleAction("accept")}
                className="!h-12 px-4 md:px-6 py-2.5 flex-1 max-w-[160px]"
              >
                Accept
              </Button>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        title="Confirm Rejection"
        description="Are you sure you want to reject this order? This action cannot be undone."
        icon={
          <div className="bg-surfaces-bg-secondary-darker rounded-full p-3">
            <X className="h-8 w-8 text-icons-icon-error" />
          </div>
        }
        confirmButton={{
          label: "Confirm",
          onClick: handleConfirmReject,
          variant: "danger",
        }}
        cancelButton={{
          label: "Cancel",
          onClick: () => setShowRejectDialog(false),
        }}
      />
    </>
  );
}
